import { useEffect, useId, useRef } from "react";
import styled from "styled-components";
import XClose from "@untitled-ui/icons-react/build/esm/XClose";
import Card from "./Card";
import FullScreenOverlay from "../overlay/PremiumFullScreenOverlay";

// Same scoped tint as DashboardHero's premium Delete Chat confirmation.
const BuyerOverlay = styled(FullScreenOverlay)`
    .fullscreen-loader { background: rgba(8,9,9,0.85); }
`;

const Shell = styled.div`
    width: min(${({ $compact }) => $compact ? "400px" : "760px"}, calc(100vw - 32px));
    min-width: 0;
    max-height: calc(100dvh - 48px);
    overflow-y: auto;
    text-align: left;
    > section { box-shadow: 0 16px 40px rgba(0,0,0,0.35); }
    header { position: relative; }
    header .premium-card-header-title { text-align: center; padding: 0 32px; }
    header .premium-card-header-right { position: absolute; right: 16px; }
    .premium-dialog-close {
        display: flex; align-items: center; justify-content: center;
        width: 32px; height: 32px; padding: 0; border: 0;
        background: transparent; color: #fff4e3; cursor: pointer;
    }
    button:focus-visible { outline: 2px solid #efbf73; outline-offset: -2px; }
    ${({ $premiumScrollbar }) => $premiumScrollbar && `
        scrollbar-width: thin; scrollbar-color: #565b5d transparent;
        &::-webkit-scrollbar { width: 6px; height: 6px; }
        &::-webkit-scrollbar-track { background: transparent; }
        &::-webkit-scrollbar-thumb { background: #565b5d; border-radius: 6px; }
    `}
    .premium-dialog-body { display: flex; flex-direction: column; gap: 16px; }
`;

const focusable = 'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/** Presentation-only modal. Callers own close guards, data and actions. */
export default function PremiumDialog({ title, accessibleName, onClose, restoreFocusRef, premiumOverlay = false, premiumScrollbar = false, compact = false, children }) {
    const titleId = useId();
    const shellRef = useRef(null);
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    const fallbackFocusRef = useRef(restoreFocusRef);
    fallbackFocusRef.current = restoreFocusRef;

    useEffect(() => {
        const previousFocus = document.activeElement;
        const shell = shellRef.current;
        const overlay = shell.closest(".fullscreen-loader");
        const isTopDialog = () => !Array.from(document.querySelectorAll(".fullscreen-loader"))
            .some(element => element !== overlay && Number(getComputedStyle(element).zIndex) > Number(getComputedStyle(overlay).zIndex));
        const getControls = () => Array.from(shell.querySelectorAll(focusable)).filter(element =>
            element.tabIndex >= 0 && !element.closest('[hidden], [aria-hidden="true"]'));
        getControls()[0]?.focus();
        const onKeyDown = event => {
            if (!isTopDialog()) return;
            if (event.key === "Escape") {
                event.preventDefault();
                event.stopImmediatePropagation();
                closeRef.current?.();
            }
            if (event.key === "Tab") {
                const controls = getControls();
                const first = controls[0], last = controls[controls.length - 1];
                if (!first) { event.preventDefault(); shell.focus(); }
                else if (event.shiftKey && (document.activeElement === first || !shell.contains(document.activeElement))) {
                    event.preventDefault(); last.focus();
                } else if (!event.shiftKey && (document.activeElement === last || !shell.contains(document.activeElement))) {
                    event.preventDefault(); first.focus();
                }
            }
        };
        const containFocus = event => {
            if (isTopDialog() && !shell.contains(event.target)) (getControls()[0] || shell).focus();
        };
        window.addEventListener("keydown", onKeyDown, true);
        document.addEventListener("focusin", containFocus);
        return () => {
            window.removeEventListener("keydown", onKeyDown, true);
            document.removeEventListener("focusin", containFocus);
            const target = previousFocus?.isConnected && previousFocus !== document.body
                ? previousFocus : fallbackFocusRef.current?.current;
            if (target?.isConnected) target.focus();
        };
    }, []);

    const Overlay = premiumOverlay ? BuyerOverlay : FullScreenOverlay;
    return <Overlay show overlayZIndex={99998} onBackdropClick={onClose}>
        <Shell $compact={compact} $premiumScrollbar={premiumScrollbar} ref={shellRef} role="dialog" aria-modal="true" aria-label={accessibleName}
            aria-labelledby={accessibleName ? undefined : titleId} tabIndex={-1}>
            <Card title={title} titleId={titleId} bodyClassName="premium-dialog-body"
                rightContent={<button className="premium-dialog-close XClose" type="button" aria-label="Close dialog" onClick={onClose}>
                    <XClose width={20} height={20} aria-hidden="true" />
                </button>}>
                {children}
            </Card>
        </Shell>
    </Overlay>;
}
